import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from "@reduxjs/toolkit";
import {
  Consumption,
  ConsumptionStats,
  ConsumptionFilters,
  TransactionFilterType,
  CountryStats,
  UserSummary,
  BillingProfileStat,
} from "../../models/Consumption";
import {
  fetchConsumption as fetchConsumptionApi,
  fetchConsumptionStats as fetchConsumptionStatsApi,
  fetchConsumptionTypes as fetchConsumptionTypesApi,
  fetchConsumptionByDate as fetchConsumptionByDateApi,
  fetchConsumptionCountries as fetchConsumptionCountriesApi,
  fetchConsumptionByCountry as fetchConsumptionByCountryApi,
  fetchConsumptionByUser as fetchConsumptionByUserApi,
  fetchConsumptionByBillingProfile as fetchConsumptionByBillingProfileApi,
} from "../../services/api";

interface ConsumptionState {
  consumption: Consumption[];
  stats: ConsumptionStats[];
  availableTypes: string[];
  loading: "idle" | "pending" | "succeeded" | "failed";
  statsLoading: "idle" | "pending" | "succeeded" | "failed";
  typesLoading: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
  statsError: string | null;
  typesError: string | null;
  pagination: {
    current_page: number;
    per_page: number;
    total_records: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  filters: ConsumptionFilters;
  countryData: CountryStats[];
  countryLoading: "idle" | "pending" | "succeeded" | "failed";
  availableCountries: string[];
  byUser: UserSummary[];
  byUserLoading: "idle" | "pending" | "succeeded" | "failed";
  byUserPagination: {
    current_page: number;
    per_page: number;
    total_records: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  byBillingProfile: BillingProfileStat[];
  byBillingProfileLoading: "idle" | "pending" | "succeeded" | "failed";
}

const emptyPagination = {
  current_page: 1, per_page: 50, total_records: 0, total_pages: 0, has_next: false, has_prev: false,
};

const initialState: ConsumptionState = {
  consumption: [],
  stats: [],
  availableTypes: [],
  loading: "idle",
  statsLoading: "idle",
  typesLoading: "idle",
  error: null,
  statsError: null,
  typesError: null,
  pagination: { ...emptyPagination },
  filters: { billingId: "", type: "all", dateFrom: null, dateTo: null, page: 1, limit: 50 },
  countryData: [],
  countryLoading: "idle",
  availableCountries: [],
  byUser: [],
  byUserLoading: "idle",
  byUserPagination: { ...emptyPagination },
  byBillingProfile: [],
  byBillingProfileLoading: "idle",
};

export const fetchConsumption = createAsyncThunk(
  "consumption/fetchConsumption",
  async (filters: ConsumptionFilters) => {
    const response = await fetchConsumptionApi(filters);
    return response;
  },
);

export const fetchConsumptionStats = createAsyncThunk(
  "consumption/fetchConsumptionStats",
  async ({
    billingId,
    dateFrom,
    dateTo,
  }: {
    billingId: string;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    const response = await fetchConsumptionStatsApi(
      billingId,
      dateFrom,
      dateTo,
    );
    return response;
  },
);

export const fetchConsumptionTypes = createAsyncThunk(
  "consumption/fetchConsumptionTypes",
  async (billingId?: string) => {
    const response = await fetchConsumptionTypesApi(billingId);
    return response;
  },
);

export const fetchConsumptionByDate = createAsyncThunk(
  "consumption/fetchConsumptionByDate",
  async ({
    billingId,
    dateFrom,
    dateTo,
    page,
    limit,
  }: {
    billingId: string;
    dateFrom: string;
    dateTo: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await fetchConsumptionByDateApi(
      billingId,
      dateFrom,
      dateTo,
      page,
      limit,
    );
    return response;
  },
);

export const fetchConsumptionCountries = createAsyncThunk(
  "consumption/fetchConsumptionCountries",
  async ({ billingId, dateFrom, dateTo }: { billingId?: string; dateFrom?: string; dateTo?: string }) => {
    const response = await fetchConsumptionCountriesApi(billingId, dateFrom, dateTo);
    return response;
  },
);

export const fetchConsumptionByCountry = createAsyncThunk(
  "consumption/fetchConsumptionByCountry",
  async ({ billingId, dateFrom, dateTo }: { billingId?: string; dateFrom?: string; dateTo?: string }) => {
    const response = await fetchConsumptionByCountryApi(billingId, dateFrom, dateTo);
    return response;
  },
);

export const fetchConsumptionByUser = createAsyncThunk(
  "consumption/fetchConsumptionByUser",
  async ({ dateFrom, dateTo, page, limit }: { dateFrom?: string; dateTo?: string; page?: number; limit?: number }) => {
    const response = await fetchConsumptionByUserApi(dateFrom, dateTo, page, limit);
    return response;
  },
);


export const fetchConsumptionByBillingProfile = createAsyncThunk(
  "consumption/fetchConsumptionByBillingProfile",
  async ({ billingId, dateFrom, dateTo }: { billingId?: string; dateFrom?: string; dateTo?: string }) => {
    const response = await fetchConsumptionByBillingProfileApi(billingId, dateFrom, dateTo);
    return response;
  },
);

const consumptionSlice = createSlice({
  name: "consumption",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearConsumption: (state) => {
      state.consumption = [];
      state.pagination = initialState.pagination;
      state.error = null;
    },
    clearStats: (state) => {
      state.stats = [];
      state.statsError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase("consumption/forceLoadingComplete", (state) => {
      state.loading = "succeeded";
      state.statsLoading = "succeeded";
      state.typesLoading = "succeeded";
    });
    // Fetch consumption
    builder
      // Fetch consumption
      .addCase(fetchConsumption.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(fetchConsumption.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.consumption = action.payload.data;
        state.pagination = action.payload.pagination;
        state.filters = {
          ...state.filters,
          ...action.payload.filters,
          type: action.payload.filters.type as TransactionFilterType,
          page: action.payload.pagination.current_page,
          limit: action.payload.pagination.per_page,
        };
      })
      .addCase(fetchConsumption.rejected, (state, action) => {
        state.loading = "failed";
        state.error =
          action.error.message || "Failed to fetch consumption data";
      })
      // Fetch consumption stats
      .addCase(fetchConsumptionStats.pending, (state) => {
        state.statsLoading = "pending";
        state.statsError = null;
      })
      .addCase(fetchConsumptionStats.fulfilled, (state, action) => {
        state.statsLoading = "succeeded";
        state.stats = action.payload.data;
      })
      .addCase(fetchConsumptionStats.rejected, (state, action) => {
        state.statsLoading = "failed";
        state.statsError =
          action.error.message || "Failed to fetch consumption stats";
      })
      // Fetch consumption types
      .addCase(fetchConsumptionTypes.pending, (state) => {
        state.typesLoading = "pending";
        state.typesError = null;
      })
      .addCase(fetchConsumptionTypes.fulfilled, (state, action) => {
        state.typesLoading = "succeeded";
        state.availableTypes = action.payload.data;
      })
      .addCase(fetchConsumptionTypes.rejected, (state, action) => {
        state.typesLoading = "failed";
        state.typesError =
          action.error.message || "Failed to fetch consumption types";
      })
      // Fetch consumption by date
      .addCase(fetchConsumptionByDate.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(fetchConsumptionByDate.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.consumption = action.payload.data;
        state.pagination = action.payload.pagination;
        state.filters = {
          ...state.filters,
          ...action.payload.filters,
          type: action.payload.filters.type as TransactionFilterType,
          page: action.payload.pagination.current_page,
          limit: action.payload.pagination.per_page,
        };
      })
      .addCase(fetchConsumptionByDate.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message || "Failed to fetch consumption data";
      })
      .addCase(fetchConsumptionCountries.pending, (state) => { state.countryLoading = "pending"; })
      .addCase(fetchConsumptionCountries.fulfilled, (state, action) => {
        state.countryLoading = "succeeded";
        state.availableCountries = action.payload.data;
      })
      .addCase(fetchConsumptionCountries.rejected, (state) => { state.countryLoading = "failed"; })
      .addCase(fetchConsumptionByCountry.pending, (state) => { state.countryLoading = "pending"; })
      .addCase(fetchConsumptionByCountry.fulfilled, (state, action) => {
        state.countryLoading = "succeeded";
        state.countryData = action.payload.data;
      })
      .addCase(fetchConsumptionByCountry.rejected, (state) => { state.countryLoading = "failed"; })
      .addCase(fetchConsumptionByUser.pending, (state) => { state.byUserLoading = "pending"; })
      .addCase(fetchConsumptionByUser.fulfilled, (state, action) => {
        state.byUserLoading = "succeeded";
        state.byUser = action.payload.data;
        state.byUserPagination = action.payload.pagination;
      })
      .addCase(fetchConsumptionByUser.rejected, (state) => { state.byUserLoading = "failed"; })
      .addCase(fetchConsumptionByBillingProfile.pending, (state) => { state.byBillingProfileLoading = "pending"; })
      .addCase(fetchConsumptionByBillingProfile.fulfilled, (state, action) => {
        state.byBillingProfileLoading = "succeeded";
        state.byBillingProfile = action.payload.data;
      })
      .addCase(fetchConsumptionByBillingProfile.rejected, (state) => { state.byBillingProfileLoading = "failed"; });
  },
});

// Selectors
export const selectConsumptionState = (state: any) => state.consumption;

export const selectConsumptionData = createSelector(
  [selectConsumptionState],
  (consumption) => consumption.consumption,
);

export const selectConsumptionStats = createSelector(
  [selectConsumptionState],
  (consumption) => consumption.stats,
);

export const selectConsumptionLoading = createSelector(
  [selectConsumptionState],
  (consumption) => consumption.loading,
);

export const selectConsumptionPagination = createSelector(
  [selectConsumptionState],
  (consumption) => consumption.pagination,
);

export const selectConsumptionFilters = createSelector(
  [selectConsumptionState],
  (consumption) => consumption.filters,
);

export const selectConsumptionAvailableTypes = createSelector(
  [selectConsumptionState],
  (consumption) => consumption.availableTypes,
);

export const { setFilters, resetFilters, clearConsumption, clearStats } =
  consumptionSlice.actions;

export default consumptionSlice.reducer;
